
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { mapQueryParams } from '@twago/query'
import { EFilter } from '@/helpers/enums'
import { updateOrCleanQuery } from '~/helpers/query'

export default {
  name: 'UserLocation',
  data() {
    return {
      items: [{}],
      menu: false,
      city: null,
      itemText: null,
      country: null,
      isValid: false,
      selectedLocationObj: null,
      geoCountries: [],
      state: null,
      cities: [],
      selectedRadius: null,
      radius: [5, 10, 25, 50, 100],
      searchCity: null,
      loading: false,
      debouncedSearch: null,
      startScrollPostion: 0,
      filterIsOpen: false
    }
  },

  computed: {
    ...mapGetters({
      getLocations: 'filters/getLocations',
      countriesList: 'countriesList'
    }),

    ...mapQueryParams({
      propName: 'selectedUserLocation',
      queryParam: EFilter.USER_LOCATION,
      serializer: (data, ctx) => ctx.serialize(data),
      deserializer: (data, ctx) => ctx.deserialize(data),
      multiple: true
    }),

    requestParams() {
      return {
        countryCode: this.country?.value.toLowerCase()
      }
    },

    sortedCountriesList() {
      return [...this.countriesList].sort((a, b) => a.text.localeCompare(b.text))
    },

    selectedLocationArr() {
      return [this.selectedLocationObj]
    },

    kmOrMiles() {
      return this.$t('talent.project.search.user.location.radius.label')
    }
  },

  watch: {
    country(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.cities = []
        this.city = null
        this.state = null
      }
    },

    async city(newVal, oldVal) {
      if (newVal) {
        await this.performGeoCodeSearch(newVal.placeId)
      } else {
        this.loading = false
      }
    },

    selectedUserLocation(newVal, oldVal) {
      if (!newVal.length) {
        return
      }
      if (newVal) {
        this.selectedUserLocation = newVal
      }
    },

    [`$route.query.${EFilter.USER_LOCATION}`](data) {
      this.userLocationSelectorFn(data)
      if (!data) {
        this.clearFilter()
      }
    },

    searchCity: function (newVal, oldVal) {
      this.loading = true

      if (!newVal) {
        this.cities = []
        return
      }
      if (
        (this.cities?.length && newVal === oldVal) ||
        !this.country ||
        this.city?.mainText === newVal
      ) {
        return
      }

      const options = {
        query: newVal ?? '',
        ...this.requestParams
      }

      this.debouncedSearch(options, 'LOCALITY')
      this.loading = false
    }
  },

  mounted() {
    this.debouncedSearch = debounce(
      async (options, type) => await this.performSearch(options, type),
      300
    )

    if (this.selectedUserLocation.length) {
      const userLocationArr = this.selectedUserLocation.split(':')
      const countryCode = userLocationArr[0]
      const country = this.countriesList.find((e) => e.value === countryCode)
      if (userLocationArr.length < 3) {
        this.selectedLocationObj = {
          text: country.text,
          value: country.text,
          countryCode,
          city: null,
          disabled: true
        }
        this.selectedRadius = Number(userLocationArr[1])
      } else {
        const city = userLocationArr[1]

        this.selectedLocationObj = {
          text: `${country.text}, ${city}`,
          value: `${country.text}, ${city}`,
          countryCode,
          city,
          disabled: true
        }

        this.selectedRadius = Number(userLocationArr[2])
      }
    }
  },

  methods: {
    serialize(data) {
      const radius = this.selectedRadius || 0

      if (data.city) {
        return encodeURIComponent(`${data.countryCode}:${data.city}:${radius}`)
      } else {
        return encodeURIComponent(`${data.countryCode}:${radius}`)
      }
    },

    deserialize(data) {
      if (!data) {
        return
      }

      return decodeURIComponent(data)
    },

    async userLocationSelectorFn(val) {
      this.$emit('before-update')

      await updateOrCleanQuery(this, val, EFilter.USER_LOCATION, val)

      this.$emit('updated')
    },

    cityClearHandle() {
      this.city = null
      this.state = null
      this.cities = []
    },

    async resetForm() {
      this.$refs.userLocationForm.reset()
      this.selectedLocationObj = null
      await this.$cleanQuery(this.$route.query, EFilter.USER_LOCATION)
    },

    async performSearch(options, type) {
      try {
        const { data } = await this.$api.utils.getGeoAutocomplete({ ...options, type })
        this.assignData(type, data)
      } catch (err) {
        console.error('err', err)
      } finally {
        this.loading = false
      }
    },

    assignData(type, data) {
      type === 'LOCALITY'
        ? (this.cities = data.map((el) => el))
        : (this.states = data.map((el) => el))
    },

    async performGeoCodeSearch(placeId) {
      try {
        const { data } = await this.$api.utils.getGeocode(encodeURIComponent(placeId))
        this.itemText = data.address.region
        this.state = data.address.region ?? null
      } catch (err) {
        console.error('err', err)
      } finally {
        this.loading = false
      }
    },

    submitForm() {
      this.$nextTick(() => {
        this.selectedLocationObj = {
          text: this.city ? `${this.country.text}, ${this.city.mainText}` : this.country.text,
          value: this.city ? `${this.country.text}, ${this.city.mainText}` : this.country.text,
          countryCode: this.country.value,
          city: this.city?.mainText,
          disabled: true
        }

        this.$refs.userLocation.blur()
        this.searchBtnHandler()
      })
    },

    searchBtnHandler() {
      if (this.selectedLocationObj) {
        this.selectedUserLocation = this.selectedLocationObj
        this.$refs.radiusForm.blur()
      }
    },

    clearFilter() {
      this.selectedLocationObj = null
      this.selectedRadius = null
      this.userLocationSelectorFn(null)
      this.$refs.userLocation.blur()
    },

    clearRadiusFilter() {
      this.selectedRadius = null
      this.$refs.radiusForm.blur()
      this.searchBtnHandler()
    },

    selectedCityText(item) {
      return item.mainText + (this.state ? ` (${this.state})` : '')
    },

    closeForm() {
      this.$refs.userLocation.blur()
    },

    closeRadiusForm() {
      this.$refs.radiusForm.blur()
    },

    closeOnScroll() {
      this.startScrollPosition = window.scrollY
      window.addEventListener('scroll', this.handleScroll)
      this.filterIsOpen = true
    },

    handleScroll() {
      const checkScrollPosition = window.scrollY

      if (
        checkScrollPosition - this.startScrollPosition > 35 ||
        checkScrollPosition - this.startScrollPosition < -30
      ) {
        this.onScrollThreshold()
      }
    },
    onScrollThreshold() {
      window.removeEventListener('scroll', this.handleScroll)
      this.closeForm()
      this.closeRadiusForm()
    },
    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
      this.filterIsOpen = false
    },

    formFocus() {
      this.closeOnScroll()
    },

    radiusFormFocus() {
      this.closeOnScroll()
    },

    focuosOnFirstAutoComplete() {
      this.$refs.countryAutocomplete.focus()
    }
  }
}
